import axios from 'axios';
import { User, UserMfaSetup } from 'models/User';
import { Tenant } from 'models/Tenant';
import {plainToClass} from 'class-transformer';
import "reflect-metadata";
import { Invoice, InvoicePaymentIdealIssuer, InvoicePaymentPortalInfo } from 'models/Invoice';
import { Email } from 'models/Email';
import { Expense, ExpenseAttachment } from 'models/Expense';
import {
    Account,
    AccountAttachment,
    AccountAttachmentRequirement,
    AccountPersonLink
} from 'models/Account';
import { InvoiceContent } from 'models/InvoiceContent';
import { Call } from "../models/Call";
import { Person } from "../models/Person";
import { InvoicePayment } from "../models/InvoicePayment";
import { Recurring } from 'models/Recurring';
import { Service, ServiceHistory } from 'models/Service';
import { ContractTemplate, Project, ProjectAttachment, ProjectWorker } from 'models/Project';
import { ExpenseDocumentMatch, MailAttachment } from 'models/Document';
import {
    Asset,
    AssetDocumentReference,
    AssetFilters,
    AssetManagerStatus,
    AssetNote,
    AssetServiceInterval,
    AssetTemplateValue
} from 'models/Asset';
import { DashboardConfig } from 'models/Dashboard';
import { TaxRecord, TaxReport, TaxSource } from 'models/Tax';

const api = axios.create();

const baseUrl = process.env.REACT_APP_OFFICE_API_URL || `https://api.office.galactic-group.nl/v1`;
const servicesUrl = process.env.REACT_APP_STATUS_API_URL || `https://api.status.galactic-group.nl/v1`;

export function loginUserAccount(email: string, password: string, mfa_token: string): Promise<string> {
    return api.post(`${baseUrl}/auth`, { email, password, mfa_token })
        .then(response => response.data.token);
}

export function getUser(): Promise<User> {
    return api.get(`${baseUrl}/user`)
        .then(response => plainToClass(User, response.data));
}

export function updateUserDashboard(dashboard: DashboardConfig): Promise<DashboardConfig> {
    return api.put(`${baseUrl}/user/dashboard`, dashboard)
        .then(response => response.data as DashboardConfig);
}

export function getTenants(): Promise<Tenant[]> {
    return api.get(`${baseUrl}/tenants`)
        .then(response => response.data.map((tenant: object): Tenant => plainToClass(Tenant, tenant)));
}

export function getUsers(): Promise<User[]> {
    return api.get(`${baseUrl}/users`)
        .then(response => response.data.map((user: object): User => plainToClass(User, user)));
}

export function getUserById(id: string): Promise<User> {
    return api.get(`${baseUrl}/users/${id}`)
        .then(response => plainToClass(User, response.data));
}

export function createUser(user: User): Promise<User> {
    return api.post(`${baseUrl}/users`, user)
        .then(response => plainToClass(User, response.data));
}

export function updateUser(user: User): Promise<User> {
    return api.put(`${baseUrl}/users/${user.id}`, user)
        .then(response => plainToClass(User, response.data));
}

export function setupUserMfa(id: string): Promise<UserMfaSetup> {
    return api.post(`${baseUrl}/users/${id}/mfa/setup`)
        .then(response => response.data);
}

export function getEmails(): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails?limit=50`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export interface EmailSearchPage {
    emails: Email[];
    from: number;
    to: number;
    searched_until: number;
    window_complete: boolean;
    next_cursor?: string;
    older_cursor?: string;
}

export function searchEmails(query: string, box: string, cursor?: string, signal?: AbortSignal): Promise<EmailSearchPage> {
    return api.get<EmailSearchPage>(`${baseUrl}/mailer/emails`, {
        params: {q: query, box, cursor, limit: 50},
        signal,
    }).then(response => ({
        ...response.data,
        emails: response.data.emails.map(mail => plainToClass(Email, mail)),
    }));
}

export function getEmail(id: string): Promise<Email> {
    return api.get(`${baseUrl}/mailer/emails/` + id)
        .then(response => plainToClass(Email, response.data));
}

export function getEmailThread(id: string): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails/${id}/thread`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function getUnreadEmails(): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/unreads`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function getEmailsAfter(date: number): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails?after=${date}&limit=50`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function getEmailsBefore(date: number): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails?before=${date}&limit=50`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function getEmailsByCustomer(customer: string): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails?customer=${customer}&limit=50`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function getEmailsByReference(reference: string): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails?reference=${encodeURIComponent(reference)}&limit=50`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function getEmailsBetween(from: number, to: number): Promise<Email[]> {
    return api.get(`${baseUrl}/mailer/emails?from=${from}&to=${to}&limit=200`)
        .then(response => response.data.map((mail: object): Email => plainToClass(Email, mail)));
}

export function fetchEmailAttachments(id: string): Promise<MailAttachment[]> {
    return api.get(`${baseUrl}/mailer/emails/${id}/attachments`)
        .then(response => (response.data ?? []).map((attachment: MailAttachment | string, index: number): MailAttachment => {
            if (typeof attachment !== "string") return attachment;
            const separator = attachment.indexOf("*");
            return {
                id: `legacy-${index}`,
                name: separator >= 0 ? attachment.slice(0, separator) : `attachment-${index + 1}`,
                legacy_content: separator >= 0 ? attachment.slice(separator + 1) : attachment,
            };
        }));
}

export const emailAttachmentContentUrl = (mailId: string, attachment: MailAttachment): string =>
    attachment.legacy_content || `${baseUrl}/mailer/emails/${mailId}/attachments/${attachment.id}/content`;

export function getEmailAttachmentContent(mailId: string, attachmentId: string): Promise<Blob> {
    return api.get(`${baseUrl}/mailer/emails/${mailId}/attachments/${attachmentId}/content`, { responseType: "blob" })
        .then(response => response.data);
}

export function starEmail(email: Email, body: any): Promise<Email> {
    return api.post(`${baseUrl}/mailer/emails/` + email.id + `/starred`, body)
        .then(response => plainToClass(Email, response.data));
}

export function setEmailCustomer(email: Email, customer: string): Promise<Email> {
    return api.post(`${baseUrl}/mailer/emails/` + email.id + `/customer`, { customer })
        .then(response => plainToClass(Email, response.data));
}

export function sendEmail(email: Email): Promise<Email> {
    return api.post(`${baseUrl}/mailer/emails`, email)
        .then(response => plainToClass(Email, response.data));
}

export function getCalls(): Promise<Call> {
    return api.get(`${baseUrl}/customers/calls`)
        .then(response => response.data.map((call: object) => plainToClass(Call, call)));
}

export function readEmail(email: Email): Promise<Email> {
    return api.post(`${baseUrl}/mailer/emails/` + email.id + `/read`)
        .then(response => plainToClass(Email, response.data));
}

export function addTenant(name: string): Promise<Tenant> {
    return api.post(`${baseUrl}/tenants`, { name })
        .then(response => plainToClass(Tenant, response.data));
}

export function getTenant(id: string): Promise<Tenant> {
    return api.get(`${baseUrl}/tenants/` + id)
        .then(response => plainToClass(Tenant, response.data));
}

export function updateTenantInvoiceSettings(
    tenantId: string,
    invoiceNameTemplate: string,
    invoicePrefix: string,
    invoiceRemindersEnabled: boolean,
): Promise<{ invoice_name_template: string; invoice_prefix: string; invoice_reminders_enabled: boolean }> {
    return api.put(`${baseUrl}/tenants/${tenantId}/invoice-settings`, {
        invoice_name_template: invoiceNameTemplate,
        invoice_prefix: invoicePrefix,
        invoice_reminders_enabled: invoiceRemindersEnabled,
    }).then(response => response.data);
}

export function updateTenantURLs(
    tenantId: string,
    urls: { account: string; payment: string },
): Promise<{ account: string; payment: string }> {
    return api.put(`${baseUrl}/tenants/${tenantId}/urls`, urls)
        .then(response => response.data);
}

export interface WebPushSubscriptionPayload {
    endpoint: string;
    clientId: string;
    expirationTime?: number | null;
    keys: {
        p256dh: string;
        auth: string;
    };
}

export function getWebPushConfig(): Promise<{ public_key: string }> {
    return api.get(`${baseUrl}/webpush/config`)
        .then(response => response.data);
}

export function subscribeTenantWebPush(tenantId: string, subscription: WebPushSubscriptionPayload): Promise<{ subscriptions: number }> {
    return api.post(`${baseUrl}/tenants/${tenantId}/webpush/subscriptions`, subscription)
        .then(response => response.data);
}

export function unsubscribeTenantWebPush(tenantId: string, endpoint: string, clientId: string): Promise<{ subscriptions: number }> {
    return api.delete(`${baseUrl}/tenants/${tenantId}/webpush/subscriptions`, { data: { endpoint, clientId } })
        .then(response => response.data);
}

export function addUserToTenant(user: string, tenantId: string): Promise<User> {
    return api.post(`${baseUrl}/tenants/` + tenantId + `/users`, { user })
        .then(response => plainToClass(User, response.data));
}

export function deleteUserFromTenant(user: string, tenantId: string): Promise<User> {
    return api.delete(`${baseUrl}/tenants/` + tenantId + `/users/` + user)
        .then(response => plainToClass(User, response.data));
}

export function getInvoicePaymentPortalInfo(id: string, token: string): Promise<InvoicePaymentPortalInfo> {
    return api.get(`${baseUrl}/payportal/` + id + `/` + token)
        .then(response => plainToClass(InvoicePaymentPortalInfo, response.data));
}

export function getPaymentIdealIssuers(id: string): Promise<InvoicePaymentIdealIssuer[]> {
    return api.get(`${baseUrl}/payportal/${id}/ideal-issuers`)
        .then(response => response.data.map((issuer: object): InvoicePaymentIdealIssuer => plainToClass(InvoicePaymentIdealIssuer, issuer)));
}

export function createIdealPayment(id: string, token: string, origin: string): Promise<{ url: string, tracker: string }> {
    return api.get(`${baseUrl}/payportal/${id}/${token}/ideal-payment?origin=${origin}`)
        .then(response => response.data);
}

export function trackPayment(id: string, tracker: string): Promise<string> {
    return api.get(`${baseUrl}/payportal/${id}/track/${tracker}`)
        .then(response => response.data.status);
}

export function getCustomers(): Promise<Account[]> {
    return getAccounts("customer");
}

export function getAccounts(role?: string, query?: string): Promise<Account[]> {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (query) params.set("query", query);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get(`${baseUrl}/accounts${suffix}`).then(response => response.data as Account[]);
}

export function getAccount(id: string): Promise<Account> {
    return api.get(`${baseUrl}/accounts/${id}`).then(response => response.data as Account);
}

export function getAssets(filters: AssetFilters = {}): Promise<Asset[]> {
    const params = new URLSearchParams();
    if (filters.tenantId) params.set("tenant_id", filters.tenantId);
    if (filters.customerId) params.set("customer_id", filters.customerId);
    if (filters.parentId) params.set("parent_id", filters.parentId);
    if (filters.query) params.set("query", filters.query);
    if (filters.type) params.set("type", filters.type);
    if (filters.status) params.set("status", filters.status);
    if (filters.criticality) params.set("criticality", filters.criticality);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get(`${baseUrl}/assets${suffix}`).then(response => response.data as Asset[]);
}

export function getAssetCalendarLink(): Promise<string> {
    return api.get(`${baseUrl}/assets/calendar-link`).then(response => {
        const apiRoot = new URL(`${baseUrl.replace(/\/+$/, "")}/`, window.location.origin);
        return new URL(String(response.data.url).replace(/^\/+/, ""), apiRoot).toString();
    });
}

export function getAsset(id: string): Promise<Asset> {
    return api.get(`${baseUrl}/assets/${id}`).then(response => response.data as Asset);
}

export function createAsset(asset: Asset): Promise<Asset> {
    const payload: Record<string, unknown> = {...asset};
    delete payload.id;
    delete payload.child_ids;
    delete payload.created_at;
    delete payload.updated_at;
    return api.post(`${baseUrl}/assets`, payload).then(response => response.data as Asset);
}

export function updateAsset(asset: Asset): Promise<Asset> {
    const payload: Record<string, unknown> = {...asset};
    delete payload.child_ids;
    return api.put(`${baseUrl}/assets/${asset.id}`, payload).then(response => response.data as Asset);
}

export function getAssetNotes(assetId: string): Promise<AssetNote[]> {
    return api.get(`${baseUrl}/assets/${assetId}/notes`).then(response => (response.data as AssetNote[])
        .map(note => ({...note, documents: note.documents ?? []})));
}

export function createAssetNote(
    assetId: string,
    note: Pick<AssetNote, "type" | "title" | "content"> & {parent_note_id?: string}
): Promise<AssetNote> {
    return api.post(`${baseUrl}/assets/${assetId}/notes`, note).then(response => response.data as AssetNote);
}

export function correctAssetNote(
    assetId: string,
    noteId: string,
    correction: {reason: string; title: string; content: string; result?: string; values?: AssetTemplateValue[]}
): Promise<AssetNote> {
    return api.put(`${baseUrl}/assets/${assetId}/notes/${noteId}`, correction).then(response => response.data as AssetNote);
}

export function updateAssetNote(
    assetId: string,
    noteId: string,
    note: Pick<AssetNote, "type" | "title" | "content">
): Promise<AssetNote> {
    return api.put(`${baseUrl}/assets/${assetId}/notes/${noteId}/edit`, note)
        .then(response => response.data as AssetNote);
}

export function uploadAssetNoteDocument(
    assetId: string,
    noteId: string,
    document: {name: string; type: string; content_type: string; content: string; signature_required: boolean}
): Promise<AssetDocumentReference> {
    return api.post(`${baseUrl}/assets/${assetId}/notes/${noteId}/documents`, document)
        .then(response => response.data as AssetDocumentReference);
}

export function deleteAssetNoteDocument(assetId: string, noteId: string, documentId: string): Promise<void> {
    return api.delete(`${baseUrl}/assets/${assetId}/notes/${noteId}/documents/${documentId}`).then(() => undefined);
}

export function getAssetNoteDocumentContentUrl(assetId: string, noteId: string, documentId: string): string {
    return `${baseUrl}/assets/${assetId}/notes/${noteId}/documents/${documentId}/content`;
}

export function getAssetNoteDocumentContent(assetId: string, noteId: string, documentId: string): Promise<Blob> {
    return api.get(getAssetNoteDocumentContentUrl(assetId, noteId, documentId), {responseType: "blob"})
        .then(response => response.data as Blob);
}

export function addAssetInterval(assetId: string, interval: Partial<AssetServiceInterval>): Promise<AssetServiceInterval> {
    const payload: Record<string, unknown> = {...interval};
    delete payload.id;
    return api.post(`${baseUrl}/assets/${assetId}/intervals`, payload).then(response => response.data as AssetServiceInterval);
}

export function updateAssetInterval(assetId: string, interval: AssetServiceInterval): Promise<AssetServiceInterval> {
    return api.put(`${baseUrl}/assets/${assetId}/intervals/${interval.id}`, interval).then(response => response.data as AssetServiceInterval);
}

export function deleteAssetInterval(assetId: string, intervalId: string): Promise<void> {
    return api.delete(`${baseUrl}/assets/${assetId}/intervals/${intervalId}`).then(() => undefined);
}

export function completeAssetInterval(
    assetId: string,
    intervalId: string,
    completion: {title: string; content: string; result: string; scheduled_at?: string; values: AssetTemplateValue[]}
): Promise<AssetNote> {
    return api.post(`${baseUrl}/assets/${assetId}/intervals/${intervalId}/complete`, completion)
        .then(response => response.data as AssetNote);
}

export function createAccount(account: Account): Promise<Account> {
    const payload: { [key: string]: any } = { ...account };
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    return api.post(`${baseUrl}/accounts`, payload).then(response => response.data as Account);
}

export function updateAccount(account: Account): Promise<Account> {
    return api.put(`${baseUrl}/accounts/${account.id}`, account).then(response => response.data as Account);
}

export function deleteAccount(id: string): Promise<void> {
    return api.delete(`${baseUrl}/accounts/${id}`).then(() => undefined);
}

export function mergeAccount(sourceId: string, targetId: string): Promise<{ account: Account; migrated: Record<string, number> }> {
    return api.post(`${baseUrl}/accounts/${sourceId}/merge`, { target_id: targetId })
        .then(response => response.data as { account: Account; migrated: Record<string, number> });
}

export function linkAccountPerson(accountId: string, personId: string, link: Partial<AccountPersonLink> = {}): Promise<Account> {
    return api.put(`${baseUrl}/accounts/${accountId}/persons/${personId}`, link).then(response => response.data as Account);
}

export function unlinkAccountPerson(accountId: string, personId: string): Promise<Account> {
    return api.delete(`${baseUrl}/accounts/${accountId}/persons/${personId}`).then(response => response.data as Account);
}

export function getAttachmentRequirements(tenantId: string): Promise<AccountAttachmentRequirement[]> {
    return api.get(`${baseUrl}/tenants/${tenantId}/attachment-requirements`).then(response => response.data);
}

export function putAttachmentRequirements(
    tenantId: string,
    requirements: AccountAttachmentRequirement[]
): Promise<AccountAttachmentRequirement[]> {
    const payload = requirements.map(requirement => {
        const value: Partial<AccountAttachmentRequirement> = { ...requirement };
        if (!value.id) delete value.id;
        return value;
    });
    return api.put(`${baseUrl}/tenants/${tenantId}/attachment-requirements`, payload)
        .then(response => response.data);
}

export function getAccountAttachments(accountId: string): Promise<AccountAttachment[]> {
    return api.get(`${baseUrl}/accounts/${accountId}/attachments`).then(response => response.data);
}

export function getAccountAttachment(accountId: string, attachmentId: string): Promise<AccountAttachment> {
    return api.get(`${baseUrl}/accounts/${accountId}/attachments/${attachmentId}`).then(response => response.data);
}

export const accountAttachmentContentUrl = (accountId: string, attachmentId: string): string =>
    `${baseUrl}/accounts/${accountId}/attachments/${attachmentId}/content`;

export function getAccountAttachmentContent(accountId: string, attachmentId: string): Promise<Blob> {
    return api.get(accountAttachmentContentUrl(accountId, attachmentId), { responseType: "blob" })
        .then(response => response.data);
}

export function deleteAccountAttachment(accountId: string, attachmentId: string): Promise<void> {
    return api.delete(`${baseUrl}/accounts/${accountId}/attachments/${attachmentId}`).then(() => undefined);
}

export function updateAccountAttachmentExpiration(
    accountId: string,
    attachmentId: string,
    expiresAt?: string
): Promise<AccountAttachment> {
    return api.put(`${baseUrl}/accounts/${accountId}/attachments/${attachmentId}`, {
        expires_at: expiresAt ? `${expiresAt}T23:59:59Z` : null,
    }).then(response => response.data);
}

export function reviewAccountAttachment(
    accountId: string,
    attachmentId: string,
    status: "approved" | "rejected",
    note = ""
): Promise<AccountAttachment> {
    return api.post(`${baseUrl}/accounts/${accountId}/attachments/${attachmentId}/review`, { status, note })
        .then(response => response.data);
}

export function getProjects(accountId?: string): Promise<Project[]> {
    const suffix = accountId ? `?account_id=${encodeURIComponent(accountId)}` : "";
    return api.get(`${baseUrl}/projects${suffix}`).then(response => response.data as Project[]);
}

export function getProject(id: string): Promise<Project> {
    return api.get(`${baseUrl}/projects/${id}`).then(response => response.data as Project);
}

export interface ProjectAddressSearchResult {
    display_name: string;
    latitude: number;
    longitude: number;
}

export function searchProjectAddresses(query: string, language?: string): Promise<ProjectAddressSearchResult[]> {
    const parameters = new URLSearchParams({ query });
    if (language) parameters.set("language", language);
    return api.get(`${baseUrl}/projects/address-search?${parameters.toString()}`)
        .then(response => response.data as ProjectAddressSearchResult[]);
}

export function createProject(project: Omit<Project, "id" | "attachment_count" | "created_at" | "updated_at">): Promise<Project> {
    return api.post(`${baseUrl}/projects`, project).then(response => response.data as Project);
}

export function updateProject(project: Project): Promise<Project> {
    return api.put(`${baseUrl}/projects/${project.id}`, project).then(response => response.data as Project);
}

export function deleteProject(id: string): Promise<void> {
    return api.delete(`${baseUrl}/projects/${id}`).then(() => undefined);
}

export function getDefaultContractTemplate(
    tenantId: string,
    templateType: ContractTemplate["type"] = "worker"
): Promise<ContractTemplate | null> {
    return api.get(`${baseUrl}/projects/contract-template/${templateType}/${tenantId}`)
        .then(response => response.data as ContractTemplate)
        .catch(reason => reason.response?.status === 404 ? null : Promise.reject(reason));
}

export function saveDefaultContractTemplate(
    tenantId: string,
    templateType: ContractTemplate["type"],
    template: Pick<ContractTemplate, "name" | "filename" | "body" | "pdf_content" | "fields" | "email_subject" | "email_body">
): Promise<ContractTemplate> {
    return api.put(`${baseUrl}/projects/contract-template/${templateType}/${tenantId}`, template)
        .then(response => response.data as ContractTemplate);
}

export function getContractTemplateVariables(): Promise<string[]> {
    return api.get(`${baseUrl}/projects/contract-template/variables`).then(response => response.data.variables as string[]);
}

export function linkProjectWorker(projectId: string, accountId: string, worker: Partial<ProjectWorker> = {}): Promise<Project> {
    return api.put(`${baseUrl}/projects/${projectId}/workers/${accountId}`, worker)
        .then(response => response.data as Project);
}

export function deleteProjectWorker(projectId: string, accountId: string): Promise<Project> {
    return api.delete(`${baseUrl}/projects/${projectId}/workers/${accountId}`)
        .then(response => response.data as Project);
}

export function getProjectWorkerAttachments(projectId: string, accountId: string): Promise<ProjectAttachment[]> {
    return api.get(`${baseUrl}/projects/${projectId}/workers/${accountId}/attachments`)
        .then(response => response.data as ProjectAttachment[]);
}

export function getProjectWorkerAttachment(projectId: string, accountId: string, attachmentId: string): Promise<ProjectAttachment> {
    return api.get(`${baseUrl}/projects/${projectId}/workers/${accountId}/attachments/${attachmentId}`)
        .then(response => response.data as ProjectAttachment);
}

export const projectWorkerAttachmentContentUrl = (projectId: string, accountId: string, attachmentId: string): string =>
    `${baseUrl}/projects/${projectId}/workers/${accountId}/attachments/${attachmentId}/content`;

export function getProjectWorkerAttachmentContent(projectId: string, accountId: string, attachmentId: string): Promise<Blob> {
    return api.get(projectWorkerAttachmentContentUrl(projectId, accountId, attachmentId), { responseType: "blob" })
        .then(response => response.data);
}

export function deleteProjectWorkerAttachment(projectId: string, accountId: string, attachmentId: string): Promise<void> {
    return api.delete(`${baseUrl}/projects/${projectId}/workers/${accountId}/attachments/${attachmentId}`)
        .then(() => undefined);
}

export function uploadProjectWorkerAttachment(
    projectId: string,
    accountId: string,
    attachment: Pick<ProjectAttachment, "name" | "kind" | "content_type"> & { content: string }
): Promise<ProjectAttachment> {
    return api.post(`${baseUrl}/projects/${projectId}/workers/${accountId}/attachments`, attachment)
        .then(response => response.data as ProjectAttachment);
}

export function getPersons(): Promise<Person[]> {
    return api.get(`${baseUrl}/persons`)
        .then(response => response.data.map((person: object): Person => plainToClass(Person, person)));
}

export function getPerson(id: string, includeSessions: boolean = false): Promise<Person> {
    return api.get(`${baseUrl}/persons/${id}${includeSessions ? "?include_sessions=true" : ""}`)
        .then(response => plainToClass(Person, response.data));
}

export function getCustomerPersons(id: string): Promise<Person[]> {
    return getAccount(id).then(account => Promise.all((account.persons ?? []).map(link =>
        getPerson(link.person_id).then(person => {
            person.accounts = Array.from(new Set([...(person.accounts ?? []), id]));
            person.customers = Array.from(new Set([...(person.customers ?? []), id]));
            return person;
        })
    )));
}

export function getManagedAssetStatus(aid: string, managerId: string, checkUpdates: boolean = false): Promise<AssetManagerStatus> {
    return api.get(`${baseUrl}/assets/${aid}/managers/${managerId}/status${checkUpdates ? "?check_updates=1" : ""}`)
        .then(response => response.data as AssetManagerStatus);
}

export function createPerson(person: Person): Promise<Person> {
    return api.post(`${baseUrl}/persons`, person)
        .then((response): Person => plainToClass(Person, response.data));
}

export function updatePerson(person: Person): Promise<Person> {
    return api.put(`${baseUrl}/persons/${person.id}?include_sessions=true`, person)
        .then((response): Person => plainToClass(Person, response.data));
}

export function createPersonAuthentication(personId: string): Promise<Person["auth"]> {
    return api.post(`${baseUrl}/persons/${personId}/authentication`)
        .then(response => plainToClass(Person, { auth: response.data }).auth);
}


export function linkPersonToCustomer(person_id: string, customer_id: string): Promise<Person> {
    return linkAccountPerson(customer_id, person_id).then(() => getPerson(person_id)).then(person => {
        person.accounts = Array.from(new Set([...(person.accounts ?? []), customer_id]));
        person.customers = Array.from(new Set([...(person.customers ?? []), customer_id]));
        return person;
    });
}

export function deletePerson(person: Person, customer: string): Promise<string> {
    return api.delete(`${baseUrl}/persons/${person.id}?customer=${customer}`)
        .then((): string => person.id);
}

export function deletePersonById(id: string): Promise<void> {
    return api.delete(`${baseUrl}/persons/${id}`).then(() => undefined);
}

export function callPerson(person: Person, tenant: string): Promise<string> {
    return api.post(`${baseUrl}/persons/${person.id}/call?tenant=${tenant}`);
}

export function getExpenses(): Promise<Expense[]> {
    return api.get(`${baseUrl}/expenses`)
        .then(response => response.data.map((expense: object): Expense => plainToClass(Expense, expense)));
}

export function getExpenseAttachments(id: string): Promise<ExpenseAttachment[]> {
    return api.get(`${baseUrl}/expenses/${id}/attachments`)
        .then(response => response.data.map((attachment: object): ExpenseAttachment => plainToClass(ExpenseAttachment, attachment)));
}

export const expenseAttachmentContentUrl = (expenseId: string, attachmentId: string): string =>
    `${baseUrl}/expenses/${expenseId}/attachments/${attachmentId}/content`;

export function getExpenseAttachmentContent(expenseId: string, attachmentId: string): Promise<Blob> {
    return api.get(expenseAttachmentContentUrl(expenseId, attachmentId), { responseType: "blob" })
        .then(response => response.data);
}

export function linkExpenseDocument(id: string, documentId: string): Promise<ExpenseAttachment[]> {
    return api.post(`${baseUrl}/expenses/${id}/attachments/link`, { document_id: documentId })
        .then(response => response.data.map((attachment: object): ExpenseAttachment => plainToClass(ExpenseAttachment, attachment)));
}

export function applyExpenseAttachmentAnalysis(id: string, attachmentId: string, fields: string[]): Promise<Expense> {
    return api.post(`${baseUrl}/expenses/${id}/attachments/${attachmentId}/apply-analysis`, { fields })
        .then(response => plainToClass(Expense, response.data));
}

export function getExpenseDocumentMatches(id: string): Promise<ExpenseDocumentMatch[]> {
    return api.get(`${baseUrl}/expenses/${id}/document-matches`).then(response => response.data);
}

export function acceptExpenseDocumentMatch(id: string, matchId: string, applyFields = true): Promise<void> {
    return api.post(`${baseUrl}/expenses/${id}/document-matches/${matchId}/accept`, { apply_fields: applyFields })
        .then(() => undefined);
}

export function addExpenseAttachment(id: string, name: string, content: string): Promise<ExpenseAttachment[]> {
    return api.post(`${baseUrl}/expenses/${id}/attachments`, { name, content })
        .then(response => response.data.map((attachment: object): ExpenseAttachment => plainToClass(ExpenseAttachment, attachment)));
}

export function deleteExpenseAttachment(id: string, aid: string): Promise<ExpenseAttachment[]> {
    return api.delete(`${baseUrl}/expenses/${id}/attachments/${aid}`)
        .then(response => response.data.map((attachment: object): ExpenseAttachment => plainToClass(ExpenseAttachment, attachment)));
}

export function addExpense(expense: Expense): Promise<Expense> {
    return api.post(`${baseUrl}/expenses`, expense)
        .then(response => plainToClass(Expense, response.data));
}

export function editExpense(expense: Expense): Promise<Expense> {
    return api.post(`${baseUrl}/expenses/` + expense.id, expense)
        .then(response => plainToClass(Expense, response.data));
}

export function getCustomer(id: string): Promise<Account> {
    return getAccount(id);
}

export function addCustomer(account: Account): Promise<Account> {
    account.roles = Array.from(new Set([...(account.roles ?? []), "customer" as const]));
    return createAccount(account);
}

export function vatCheck(vatNumber: string): Promise<any> {
    return api.get(`${baseUrl}/accounts/vat_check/${vatNumber}`)
        .then(response => response.data);
}

export function kvkCheck(kvkNumber: string, refresh = false): Promise<any> {
    return api.get(`${baseUrl}/accounts/kvk_check/${encodeURIComponent(kvkNumber)}`, refresh ? {
        params: { refresh: true },
    } : undefined)
        .then(response => response.data);
}

export function editCustomer(account: Account): Promise<Account> {
    account.roles = Array.from(new Set([...(account.roles ?? []), "customer" as const]));
    return updateAccount(account);
}

export function getInvoices(): Promise<Invoice[]> {
    return api.get(`${baseUrl}/invoices`)
        .then(response => (Array.isArray(response.data) ? response.data : [])
            .map((invoice: object): Invoice => plainToClass(Invoice, invoice)));
}

export function getProjectInvoices(projectId: string): Promise<Invoice[]> {
    return api.get(`${baseUrl}/invoices?project=${encodeURIComponent(projectId)}`)
        .then(response => (Array.isArray(response.data) ? response.data : [])
            .map((invoice: object): Invoice => plainToClass(Invoice, invoice)));
}

export function getOrphanedPayments(): Promise<InvoicePayment[]> {
    return api.get(`${baseUrl}/payments/orphaned`)
        .then(response => response.data.map((payment: object): InvoicePayment => plainToClass(InvoicePayment, payment)));
}

export function linkOrphanedPayment(payment: string, invoice: string): Promise<Invoice> {
    return api.post(`${baseUrl}/payments/orphaned/${payment}`, { invoice })
        .then(response => plainToClass(Invoice, response.data));
}

export function getInvoice(id: string): Promise<Invoice> {
    return api.get(`${baseUrl}/invoices/${id}`)
        .then(response => plainToClass(Invoice, response.data));
}

export function getInvoiceExport(id: string): Promise<string> {
    return api.get(`${baseUrl}/invoices/${id}/export/pdf`)
        .then(response => response.data.pdf);
}

export interface InvoiceContentSuggestion {
    type: string;
    uom: string;
    price: number;
    tax_rate: number;
}

export function getInvoiceContentSuggestions(customer: string, q: string, excludeInvoice?: string): Promise<InvoiceContentSuggestion[]> {
    const exclude = excludeInvoice ? `&exclude=${encodeURIComponent(excludeInvoice)}` : "";
    return api.get(`${baseUrl}/invoices/content-suggestions?customer=${encodeURIComponent(customer)}&q=${encodeURIComponent(q)}${exclude}`)
        .then(response => response.data);
}

export function addInvoice(i: Invoice): Promise<Invoice> {
    return api.post(`${baseUrl}/invoices`, i)
        .then(response => plainToClass(Invoice, response.data));
}

export interface ManualInvoicePayment {
    date: string;
    iban?: string;
    amount: number;
}

export function addInvoicePayment(invoiceId: string, payment: ManualInvoicePayment): Promise<Invoice> {
    return api.post(`${baseUrl}/invoices/${invoiceId}/payment`, payment)
        .then(response => plainToClass(Invoice, response.data));
}

export function addInvoiceContent(c: InvoiceContent, invoiceId: string): Promise<Invoice> {
    return api.post(`${baseUrl}/invoices/` + invoiceId + `/content`, c)
        .then(response => plainToClass(Invoice, response.data));
}


export function deleteInvoiceContent(i: Invoice, contentId: string): Promise<Invoice> {
    return api.delete(`${baseUrl}/invoices/` + i.id + `/content/` + contentId)
        .then(response => plainToClass(Invoice, response.data));
}

export function deleteInvoice(i: Invoice): Promise<any> {
    return api.delete(`${baseUrl}/invoices/` + i.id)
        .then(response => i.id);
}

export function deleteInvoiceById(id: string): Promise<void> {
    return api.delete(`${baseUrl}/invoices/${id}`).then(() => undefined);
}

export function deleteEmail(email: Email): Promise<any> {
    return api.delete(`${baseUrl}/mailer/emails/` + email.id)
        .then(response => email.id);
}

export function convertInvoice(i: Invoice): Promise<Invoice> {
    return api.post(`${baseUrl}/invoices/` + i.id + `/convert`)
        .then(response => plainToClass(Invoice, response.data));
}

export function finalizeInvoice(i: Invoice): Promise<Invoice> {
    return api.post(`${baseUrl}/invoices/` + i.id + `/finalize`)
        .then(response => plainToClass(Invoice, response.data));
}


export function editBasicDataInvoice(d: any): Promise<Invoice> {
    return api.post(`${baseUrl}/invoices/` + d.id, d)
        .then(response => plainToClass(Invoice, response.data));
}


export function getRecurrings(): Promise<Recurring[]> {
    return api.get(`${baseUrl}/invoices/recurrings`)
        .then(response => (response.data ?? []).map((recurring: object): Recurring => plainToClass(Recurring, recurring)));
}

export function addRecurring(recurring: Recurring): Promise<Recurring> {
    return api.post(`${baseUrl}/invoices/recurrings`, recurring)
        .then(response => plainToClass(Recurring, response.data));
}

export function updateRecurring(recurring: Recurring): Promise<Recurring> {
    return api.put(`${baseUrl}/invoices/recurrings/${recurring.id}`, recurring)
        .then(response => plainToClass(Recurring, response.data));
}


export function deleteRecurring(recurring: Recurring): Promise<string> {
    return api.delete(`${baseUrl}/invoices/recurrings/${recurring.id}`)
        .then((): string => recurring.id);
}

export function getServices(): Promise<Service[]> {
    return api.get(`${servicesUrl}/services`)
        .then(response => response.data.map((service: object): Service => plainToClass(Service, service)))
        .catch(e => {
            console.error(e);
            return [];
        })
}

export function addService(service: Service): Promise<Service> {
    return api.post(`${servicesUrl}/services`, service)
        .then(response => plainToClass(Service, response.data));
}

export function editService(service: Service): Promise<Service> {
    return api.put(`${servicesUrl}/services/` + service.id, service)
        .then(response => plainToClass(Service, response.data));
}

export function removeService(service: Service): Promise<Service> {
    return api.delete(`${servicesUrl}/services/` + service.id)
        .then(response => plainToClass(Service, response.data));
}

export function getService(id: string): Promise<Service> {
    return api.get(`${servicesUrl}/services/` + id)
        .then(response => plainToClass(Service, response.data));
}

export function getServiceHistory(id: string): Promise<ServiceHistory> {
    return api.get(`${servicesUrl}/services/${id}/history`)
        .then(response => response.data);
}

export default api;

export function getTaxRecords(tenant: string, asof: string): Promise<TaxRecord[]> {
    return api.get(`${baseUrl}/tax/${tenant}/records`, {params: {asof}}).then(response => response.data);
}
export function getTaxSources(tenant: string, source?: "invoice_payment"): Promise<TaxSource[]> {
    return api.get(`${baseUrl}/tax/${tenant}/sources`, {params: source ? {source} : undefined}).then(response => response.data);
}
export function saveTaxRecord(record: TaxRecord): Promise<TaxRecord> {
    const url = `${baseUrl}/tax/${record.tenant}/records`;
    return (record.id ? api.put(`${url}/${record.id}`, record) : api.post(url, record)).then(response => response.data);
}
export function deleteTaxRecord(tenant: string, id: string): Promise<void> {
    return api.delete(`${baseUrl}/tax/${tenant}/records/${id}`).then(() => undefined);
}
export function getTaxReport(tenant: string, start: string, end: string): Promise<TaxReport> {
    return api.get(`${baseUrl}/tax/${tenant}/report`, {params: {start, end}}).then(response => response.data);
}
export function getAnnualAccountsPDF(tenant: string, start: string, end: string): Promise<Blob> {
    return api.get(`${baseUrl}/tax/${tenant}/pdf`, {params: {start, end}, responseType: "blob"}).then(response => response.data);
}

export function markEmailAsSpam(email: Email): Promise<string> {
    return api.post(`${baseUrl}/mailer/emails/${email.id}/spam`).then(() => email.id);
}
